import type { WhatsappHierarchyNode } from "../components/WhatsappDropdown/WhatsappDropdown";
import type { AntiBotMeta } from "../constants/antiBot";
import type { UnitGroupConfig, UnitGroupingMode } from "@autodromo/units";
import type { TagItem } from "./tags";
import type { SubscriptionCoupon } from "./coupons";
import type { SubscriptionOptional } from "./optionals";

/**
 * Interface para tipografia do styleguide
 */
export interface TypographyConfig {
  fontFamily: string;
  fontWeight: number;
  fontSize: {
    mobile: number;
    desktop: number;
  };
  lineHeight: number;
  letterSpacing: string;
}

/**
 * Interface para o design do styleguide
 */
export interface DesignConfig {
  button: {
    borderRadius: string;
    borderWidth: string;
  };
  card: {
    borderRadius: string;
    borderWidth: string;
  };
}

/**
 * Interface para uma paleta de cores
 */
export interface PaletteConfig {
  general: {
    primary: string;
    background: string;
    border: string;
  };
  text: {
    title: string;
    body: string;
  };
  cards: {
    background: string;
    title: string;
    body: string;
    border: string;
  };
  buttons: {
    background: string;
    text: string;
    border: string;
  };
}

/**
 * Interface para o styleguide completo
 */
export interface Styleguide {
  typographies: {
    HEADING: TypographyConfig;
    SUB_HEADING: TypographyConfig;
    TITLE: TypographyConfig;
    TEXT: TypographyConfig;
  };
  design: DesignConfig;
  palettes: {
    [key: string]: PaletteConfig;
  };
}

/**
 * Interface para unidade
 */
export interface UnitPhone {
  id: string;
  name: string;
  order: number;
  parentId: string | null;
  phoneNumber: string | null;
  type?: "phone" | "whatsapp";
  message?: string | null;
  children?: UnitPhone[];
}

export interface UnitOperatingHour {
  id: string;
  dayOfWeek: string;
  hours: string;
}

export interface UnitSocialMedia {
  id: string;
  url: string;
  icon: string | null;
}

export interface Unit {
  id: string;
  name: string;
  displayName: string;
  state: string | null;
  city: string | null;
  phoneNumber: string | null;
  phones?: UnitPhone[];
  active?: boolean;
  imageUrl?: string | null;
  logoUrl?: string | null;
  email?: string | null;
  corporateName?: string | null;
  cnpj?: string | null;
  description?: string | null;
  zipCode?: string | null;
  address?: string | null;
  neighborhood?: string | null;
  number?: string | null;
  complement?: string | null;
  mapLink?: string | null;
  mapEmbed?: string | null;
  operatingHours?: UnitOperatingHour[];
  socialMedia?: UnitSocialMedia[];
  phonesList?: string;
  hoursFormatted?: string;
}

export interface UnitSocialMedia {
  id: string;
  url: string;
  icon: string | null;
}

export interface UnitOperatingHour {
  id: string;
  dayOfWeek: string;
  hours: string;
}

/**
 * Unidade como publicada no `channel.json` — a projeção completa, com endereço,
 * documentos e contatos. Distinta do `Unit` acima, que reflete a projeção
 * reduzida do `metadata.json` (7 campos).
 */
export interface ChannelUnit {
  id: string;
  publicId: string | null;
  name: string;
  displayName: string;
  cnpj: string | null;
  corporateName: string | null;
  description: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
  logoUrl: string | null;
  logoAlt: string | null;
  country: string | null;
  zipCode: string | null;
  state: string | null;
  city: string | null;
  address: string | null;
  neighborhood: string | null;
  number: string | null;
  complement: string | null;
  mapEmbed: string | null;
  mapLink: string | null;
  latitude: number | null;
  longitude: number | null;
  email: string | null;
  phones: UnitPhone[] | null;
  operatingHours: UnitOperatingHour[] | null;
  socialMedia: UnitSocialMedia[] | null;
  active: boolean | null;
}

/**
 * Identidade estruturada do site, publicada no `channel.json`.
 *
 * `parentName`/`parentUrl` são o grupo controlador. O publish parou de emiti-los
 * enquanto a configuração não migra para o nível da conta — a hosting-api não
 * alcança a general-api, onde vive a `Account`. O contrato fica opcional porque
 * o gerador continua sabendo montar o nó quando os dados voltarem.
 */
export interface ChannelOrganization {
  name: string | null;
  logo: string | null;
  socialMedia: UnitSocialMedia[] | null;
  parentName?: string | null;
  parentUrl?: string | null;
}

/** Conteúdo do `channel.json` publicado pelo hosting-api. */
export interface ChannelData {
  id: string;
  publicId: string;
  name: string | null;
  slug: string | null;
  domain: string | null;
  brandPublicId: string | null;
  /** Id interno da marca do site; vai no corpo do lead para o filtro de marca do CRM. */
  brandId?: string | null;
  brandName: string | null;
  publishedAt: string | null;
  organization?: ChannelOrganization | null;
  // Selos e cupons de assinatura da conta, publicados no channel.json para os blocos
  // do Portal de Assinaturas resolverem no site publicado (ATW-545).
  subscriptionConfig?: { badges: TagItem[]; coupons?: SubscriptionCoupon[]; optionals?: SubscriptionOptional[] } | null;
  units: ChannelUnit[];
}

/**
 * Interface para os metadados do site
 */
export type UnitDisplayMode = "UNIT_ONLY" | "UNIT_AND_UNASSIGNED";

export type {
  UnitGroupingMode,
  UnitGroupConfig,
  UnitGroupView,
} from "@autodromo/units";

export interface SiteMetadata {
  api_key: string | null;
  channelId: string | null;
  channelPublicId: string | null;
  accountId: string | null;
  brandPublicId: string | null;
  apiKey: string | null;
  name: string | null;
  favicon: string | null;
  scriptsHead: string | null;
  scriptsFooter: string | null;
  cookieButtonText: string | null;
  cookieButtonColor: string | null;
  cookieDescription: string | null;
  enableCookies: boolean;
  facebookPixelId: string | null;
  googleAnalyticsId: string | null;
  googleTagManagerId: string | null;
  antiBot?: AntiBotMeta;
  enableNoIndex: boolean;
  disableCentralAnalytics?: boolean;
  // Tag de verificação do Google Search Console (ATW-425). Emitida como
  // <meta name="google-site-verification"> no head do site publicado. Opcional
  // para retrocompat com JSONs publicados antes da feature.
  gscVerificationTag?: string | null;
  unitPopupEnabled: boolean;
  unitPopupRequired: boolean;
  unitPopupTitle: string | null;
  unitPopupDescription: string | null;
  unitPopupColor: string | null;
  // Padrão do canal para filtragem de conteúdo por unidade. Ausente em JSONs
  // publicados antes da feature → o consumidor assume "UNIT_ONLY".
  unitDisplayMode?: UnitDisplayMode;
  // Como as unidades se agrupam no seletor do topo e no pop-up de primeiro
  // acesso. Ausente em JSONs publicados antes da feature -> o consumidor
  // assume "NONE", que e a lista plana de sempre.
  unitGroupingMode?: UnitGroupingMode;
  unitGroups?: UnitGroupConfig[];
  unitGroupingLabel?: string | null;
  units: Unit[];
  whatsapp?: {
    floatingButtonEnabled: boolean;
    headerButtonEnabled: boolean;
    floatingButtonPosition: "BOTTOM_RIGHT" | "BOTTOM_LEFT";
    buttonColor: string;
    iconColor: string;
    endpointUrl: string | null;
    visibilityMode: "VISIBLE_ON_ALL" | "CUSTOM_PER_PAGE";
    items: WhatsappHierarchyNode[];
  };
}

/**
 * Cache em memória para metadata por domínio.
 * A validação de atualização é delegada ao S3 via conditional GET (If-None-Match):
 * cada request envia o ETag armazenado e o S3 responde 304 se inalterado,
 * ou 200 com o novo conteúdo.
 */
const metadataCache: Map<string, { data: SiteMetadata; etag: string }> =
  new Map();

/**
 * Remove https://, http:// do domínio
 */
export function cleanDomain(host: string): string {
  return host
    .replace(/^https?:\/\//, "") // Remove http:// ou https://
    .replace(/\/+$/, ""); // Remove barra(s) final(is) para não gerar // nas URLs
}

/**
 * Constrói a URL do S3 para buscar o JSON da página
 */
export function buildS3Url(domain: string, filename: string): string {
  const cleanedDomain = cleanDomain(domain);
  if (process.env.LOCAL_S3_OVERRIDE) {
    return `${process.env.LOCAL_S3_OVERRIDE}/${cleanedDomain}/${filename}`;
  }
  const url = `https://${process.env.S3_BUCKET}.s3.us-east-1.amazonaws.com/${cleanedDomain}/${filename}`;
  return url;
}

/**
 * Busca o JSON do S3
 */
export async function fetchPageFromS3(
  domain: string,
  filename: string,
): Promise<any | null> {
  try {
    const url = buildS3Url(domain, filename);

    const response = await fetch(url, {});

    if (!response.ok) {
      console.error(
        `Erro ao buscar do S3: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    const json = await response.json();

    // Retorna o JSON completo
    return json;
  } catch (error) {
    console.error("Erro ao buscar página do S3:", error);
    return null;
  }
}

/**
 * Busca metadata.json do S3 com cache em memória
 */
export async function fetchMetadata(
  domain: string,
): Promise<SiteMetadata | null> {
  const cleanedDomain = cleanDomain(domain);
  const cached = metadataCache.get(cleanedDomain);

  try {
    const url = buildS3Url(domain, "metadata.json");
    const headers: Record<string, string> = {};

    if (cached) {
      headers["If-None-Match"] = cached.etag;
    }

    const response = await fetch(url, { headers });

    if (response.status === 304 && cached) {
      return cached.data;
    }

    if (!response.ok) {
      console.error(
        `Erro ao buscar metadata do S3: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    const metadata: SiteMetadata = await response.json();
    const etag = response.headers.get("etag");

    if (etag) {
      metadataCache.set(cleanedDomain, { data: metadata, etag });
    }

    return metadata;
  } catch (error) {
    console.error("Erro ao buscar metadata do S3:", error);
    return null;
  }
}

/**
 * Busca styleguide.json do S3 (sem cache)
 */
export async function fetchStyleguide(
  domain: string,
): Promise<Styleguide | null> {
  try {
    const url = buildS3Url(domain, "styleguide.json");
    const response = await fetch(url, {});

    if (!response.ok) {
      console.error(
        `Erro ao buscar styleguide do S3: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    const styleguide: Styleguide = await response.json();
    return styleguide;
  } catch (error) {
    console.error("Erro ao buscar styleguide do S3:", error);
    return null;
  }
}

export async function fetchFormFromS3(formId: string): Promise<any | null> {
  try {
    const url = `https://${process.env.S3_FORMS_BUCKET}.s3.us-east-1.amazonaws.com/forms/${formId}.json`;
    const response = await fetch(url, {});

    if (!response.ok) {
      console.error(
        `Erro ao buscar form do S3: ${response.status} ${response.statusText}`,
      );
      return null;
    }

    const form = await response.json();
    return form;
  } catch (error) {
    console.error(`Erro ao buscar form ${formId} do S3:`, error);
    return null;
  }
}

export function extractFormIds(pageData: any): string[] {
  if (!pageData) return [];

  const formIds: Set<string> = new Set();

  function traverse(nodeId: string) {
    const node = pageData[nodeId];
    if (!node) return;

    const typeName =
      typeof node.type === "object" ? node.type.resolvedName : node.type;

    // Precisa cobrir todos os blocos de formulario que o Node.tsx resolve por
    // selectedFormId (DynamicFormWrapper, FormV2Wrapper e
    // DynamicFormWithGalleryWrapper); caso contrario o form nao e buscado no S3
    // e o site mostra "Nenhum formulário encontrado.".
    if (
      (typeName === "DynamicFormWrapper" ||
        typeName === "FormV2Wrapper" ||
        typeName === "DynamicFormWithGalleryWrapper") &&
      node.props?.selectedFormId
    ) {
      formIds.add(node.props.selectedFormId);
    }

    if (node.nodes && Array.isArray(node.nodes)) {
      node.nodes.forEach((childId: string) => traverse(childId));
    }
  }

  if (pageData.ROOT) {
    traverse("ROOT");
  }

  return Array.from(formIds);
}

// Coleta os selectedFormId dos blocos Formulário (FormBlockWrapper) dentro das
// estruturas dos popups publicados, para o renderer buscar esses forms no S3 igual
// aos das páginas. Recebe o array de popups (cada um com sua `structure`).
export function extractPopupFormIds(popups: any[]): string[] {
  if (!Array.isArray(popups)) return [];

  const formIds: Set<string> = new Set();

  for (const popup of popups) {
    const structure = popup?.structure;
    if (!structure || typeof structure !== "object") continue;

    for (const node of Object.values(structure) as any[]) {
      const typeName =
        typeof node?.type === "object" ? node.type.resolvedName : node?.type;
      if (typeName === "FormBlockWrapper" && node?.props?.selectedFormId) {
        formIds.add(node.props.selectedFormId);
      }
    }
  }

  return Array.from(formIds);
}

export async function fetchMultipleForms(
  formIds: string[],
): Promise<Record<string, any | null>> {
  if (formIds.length === 0) return {};

  const results = await Promise.all(
    formIds.map(async (formId) => {
      const form = await fetchFormFromS3(formId);
      return { formId, form };
    }),
  );

  const formsMap: Record<string, any | null> = {};
  results.forEach(({ formId, form }) => {
    formsMap[formId] = form;
  });

  return formsMap;
}

const homePageEtagCache: Map<string, string> = new Map();

export function clearHomePageCache(): void {
  homePageEtagCache.clear();
}

export async function hasPublishedHomePage(domain: string): Promise<boolean> {
  const cleanedDomain = cleanDomain(domain);
  const cachedEtag = homePageEtagCache.get(cleanedDomain);

  try {
    const url = buildS3Url(domain, "index.json");
    const headers: Record<string, string> = {};

    if (cachedEtag) {
      headers["If-None-Match"] = cachedEtag;
    }

    const response = await fetch(url, { headers });

    if (response.status === 304 && cachedEtag) {
      return true;
    }

    if (response.status === 404 || response.status === 403) {
      homePageEtagCache.delete(cleanedDomain);
      return false;
    }

    if (!response.ok) {
      console.error(
        `Erro ao verificar a home do canal no S3: ${response.status} ${response.statusText}`,
      );
      return cachedEtag !== undefined;
    }

    const etag = response.headers.get("etag");
    if (etag) {
      homePageEtagCache.set(cleanedDomain, etag);
    }

    return true;
  } catch (error) {
    console.error("Erro ao verificar a home do canal no S3:", error);
    return cachedEtag !== undefined;
  }
}
